import React from "react"
import { Slot } from "@radix-ui/react-slot"

import { sanitizeHtml } from "@/lib/sanitizeHtml"
import { cn, parseSimpleString, transformButtonStyle } from "@/lib/utils"
import { Feature, IFeatureProps, Link, TButton } from "@/components/ui/atoms"

interface IFeatureListProps {
  asChild?: boolean
  title: string
  features: Array<IFeatureProps>
  buttons?: TButton[]
  isInArticle?: boolean
}

const FeatureList = React.forwardRef<HTMLDivElement, IFeatureListProps>(
  (
    {
      asChild = false,
      title,
      features,
      buttons,
      isInArticle = false,
      ...props
    },
    ref
  ) => {
    const Component = asChild ? Slot : "div"
    return (
      <Component
        className={cn(isInArticle ? "" : "container py-12")}
        ref={ref}
        {...props}
      >
        <div
          className={cn(
            "feature-list bg-neutral-100",
            isInArticle ? "mb-11" : "mb-13 lg:mb-17"
          )}
        >
          <div className="flex justify-center">
            <div className="feature-list__title heading-s">
              <span
                dangerouslySetInnerHTML={{ __html: sanitizeHtml(title) }}
              ></span>
            </div>
          </div>
          <div
            className={cn(
              "feature-list__features",
              features.length % 3 === 0 ? "feature-list__features--three" : "",
              features.length === 2 ? "feature-list__features--two" : "",
              buttons && buttons?.length === 0
                ? "feature-list__features--nolinks"
                : ""
            )}
          >
            {features.map((feature) => {
              return (
                <Feature
                  key={feature.title}
                  icon={feature.icon}
                  title={feature.title}
                  text={feature.text}
                  link={feature.link}
                  linkIconLeft={feature.linkIconLeft}
                  linkIconRight={feature.linkIconRight}
                  linkText={feature.linkText}
                />
              )
            })}
          </div>
          {buttons && buttons?.length > 0 ? (
            <div className="feature-list__links">
              {buttons
                ? buttons.map((button) => {
                    return (
                      <Link
                        chapters={[title, button.ctaLabel]}
                        key={button.codename}
                        title={parseSimpleString(title)}
                        variant={transformButtonStyle(button.style)}
                        iconLeft={button.iconLeft ?? undefined}
                        iconRight={button.iconRight ?? undefined}
                        href={button.ctaLink}
                      >
                        {button.ctaLabel}
                      </Link>
                    )
                  })
                : null}
            </div>
          ) : null}
        </div>
      </Component>
    )
  }
)
FeatureList.displayName = "FeatureList"

export { FeatureList, type IFeatureListProps }
